import clsx from "clsx";
import { useBanner } from "@/hooks/useBanner";
import { useTranslation } from "react-i18next";

const ChatSkeleton = ({
  /** Fill the parent element instead of viewport-height layout (dock mode). */
  fillContainer = false,
  sheet = false,
}: {
  fillContainer?: boolean;
  sheet?: boolean;
}) => {
  const { isBannerActive } = useBanner();
  const { t } = useTranslation("ask");

  return (
    <div
      className={clsx(
        "flex flex-col",
        sheet ? "pb-4" : "pb-6",
        // Mirrors ChatView's sizing: embedded surfaces (assistant dock) own
        // the height; standalone pages size against the viewport. Without
        // this the skeleton overflows the dock and crops the input shape.
        fillContainer && "h-full",
        !fillContainer &&
          (isBannerActive
            ? "md:h-[calc(100vh-70px)] h-[calc(100vh-140px)]"
            : "h-[calc(100vh-70px)] md:h-[100vh]"),
        "mx-auto justify-center items-center"
      )}
    >
      {/* Scrollable messages area skeleton */}
      <div className="flex-grow overflow-y-auto w-full relative pt-8">
        <div className="max-w-6xl mx-auto">
          {/* User message skeleton */}
          <div className="mb-4 px-3 flex justify-end">
            <div className="max-w-[75%] bg-gailPrimary rounded-lg p-4 py-2 animate-pulse ml-auto w-full">
              <div className="space-y-2">
                <div className="h-4 bg-blue-400 rounded w-2/3"></div>
              </div>
            </div>
          </div>

          {/* Assistant message skeleton */}

          <div className="mb-4 px-3">
            <div className="max-w-[75%] rounded-2xl p-4 animate-pulse">
              <div className="flex items-center mb-2">
                <div className="w-6 h-6 bg-black/60 rounded-full mr-2"></div>
                <div className="h-4 bg-black/60 rounded w-20"></div>
              </div>
              <div className="space-y-2">
                <div className="h-4 bg-black/60 rounded w-full"></div>
                <div className="h-4 bg-black/60 rounded w-2/3"></div>
              </div>
              <div className="space-y-2 mt-4">
                <div className="h-4 bg-black/60 rounded w-3/4"></div>
                <div className="h-4 bg-black/60 rounded w-full"></div>
                <div className="h-4 bg-black/60 rounded w-5/6"></div>
              </div>
            </div>
          </div>
        </div>
      </div>

      {/* Fixed input skeleton at bottom */}
      <div className={clsx("mt-auto w-full", sheet ? "px-4 pb-2" : "transition-opacity duration-300 p-2 opacity-100 max-w-6xl")}>
        <div className="bg-background rounded-2xl shadow-sm border border-neutral p-4 animate-pulse">
          <div className="flex items-center space-x-3">
            {/* Input area skeleton */}
            <div className="flex-1 h-20 bg-background rounded-lg"></div>

            {/* Send button skeleton */}
            <div className="w-10 h-10 bg-background/80 rounded-lg"></div>
          </div>
        </div>
      </div>
      {sheet && <p className="shrink-0 px-4 text-center text-xs text-600">{t("chatDisclaimer")}</p>}
    </div>
  );
};

export default ChatSkeleton;
